import { isDefined, SelectedAccountOrClient } from '@eih-netbroker/common';
import { Popover } from '@mui/material';
import React, { createContext, useCallback, useLayoutEffect, useState } from 'react';

import { Account, AccountCategory, accountsAtom } from '@eih-netbroker/common';
import { useAtomValue } from 'jotai';
import { AccountsPopoverContent } from '../modules/core/components/AccountsPopoverContent';
import classes from '../modules/core/partials/Header.module.scss';
import { useUserSettings } from '../shared/services/user-settings/useUserSettings';
import { useLogger } from '../shared/utils/useLogger';

export type AccountContextProps =
  | undefined
  | {
      selectedAccountOrClient?: SelectedAccountOrClient;
      initAccountSelectorButton: (button: HTMLButtonElement | null) => void;
      openAccountSelector: () => void;
      getSelectedAccount<T extends boolean = false>(
        allowMultipleAccountSelection?: T,
        disabledCategories?: Array<AccountCategory>,
      ): Promise<T extends true ? Array<Account> : Account | null>;
      selectAccount: (account: Account | string) => void;
      selectClient: (client?: string) => void;
      isAccountSelectorOpen: boolean;
      getAccountsOfClient: (clientId: string) => Array<Account>;
      getAccountsOfClientByAccount: (accountNumber: string) => Array<Account>;
      closeAccountPopover: () => void;
    };

export const AccountContext = createContext<AccountContextProps>(undefined);

export const AccountProvider = ({ children }: { children: React.ReactNode }) => {
  const accounts = useAtomValue(accountsAtom);
  const [accountSelectorButton, setAccountSelectorButton] = useState<HTMLButtonElement | null>(null);
  const [accountSelectorPopover, setAccountSelectorPopover] = useState<HTMLButtonElement | null>(null);
  const [selectAllAccountEnabled, setSelectAllAccountEnabled] = useState<boolean>(false);
  const [allowClientSelection, setAllowClientSelection] = useState<boolean>(true);
  const [selectedAccountOrClient, setSelectedAccountOrClient] = useState<SelectedAccountOrClient>();
  const { getLastSelectedAccount, updateLastSelectedAccount } = useUserSettings();
  const [disabledAccountCategories, setDisabledAccountCategories] = useState<Array<AccountCategory>>([]);
  const logger = useLogger('AccountProvider');

  const [accountSelectPromise, setAccountSelectPromise] = useState<{
    resolve: (value: Array<Account> | Account | null) => void;
    reject: () => void;
  } | null>(null);

  /**
   * Returns the accounts of a client
   */
  const getAccountsOfClient = useCallback(
    (clientId: string) => {
      if (!accounts.isLoaded) {
        throw new Error('Accounts are not loaded');
      }
      const clients = accounts.clients;
      const selectedClient = clients.find(client => client.clientId === clientId);
      if (!selectedClient) {
        logger.error(`No client selected with id: ${clientId}`, { clients });
        throw new Error(`No client selected with id: ${clientId}`);
      }
      return selectedClient.accounts;
    },
    [accounts, logger],
  );

  const updateSelectedAccountOrClient = useCallback(
    (selectedAccount: SelectedAccountOrClient) => {
      setSelectedAccountOrClient(selectedAccount);
      updateLastSelectedAccount(selectedAccount);
    },
    [setSelectedAccountOrClient, updateLastSelectedAccount],
  );

  /**
   * Returns the accounts of client by account
   */
  const getAccountsOfClientByAccount = useCallback(
    (accountNumber: string) => {
      if (!accounts.isLoaded) {
        throw new Error('Accounts are not loaded');
      }
      const clients = accounts.clients;
      const selectedClient = clients.find(client => client.accounts.some(acc => acc.accountNumber === accountNumber));
      if (!selectedClient) {
        logger.error(`No account exist: ${accountNumber}`, { clients });
        throw new Error(`No account exist: ${accountNumber}`);
      }
      return selectedClient.accounts;
    },
    [accounts, logger],
  );

  /**
   * Handles the account selector popover close event
   *  - resolves the accountSelectPromise with the selected account or client (if there is any)
   *  - updates the selected account or client
   * @private
   */
  const handleAccountsClose = useCallback(
    (result?: SelectedAccountOrClient) => {
      if (result) {
        updateSelectedAccountOrClient(result);
      }
      if (accountSelectPromise) {
        if (result && ((!allowClientSelection && result.account) || (allowClientSelection && result.client))) {
          accountSelectPromise.resolve(result?.account || getAccountsOfClient(result!.client));
        } else {
          accountSelectPromise.resolve(null);
        }
        setAccountSelectPromise(null);
      }

      setAllowClientSelection(true);
      setAccountSelectorPopover(null);
    },
    [accountSelectPromise, getAccountsOfClient, allowClientSelection, updateSelectedAccountOrClient],
  );

  const closeAccountPopover = useCallback(() => {
    handleAccountsClose(undefined);
  }, [handleAccountsClose]);

  /**
   * Selects an account
   * @param account Account DTO or an account number
   */
  const selectAccount = useCallback(
    (account: Account | string) => {
      if (!accounts.isLoaded) {
        throw new Error('Accounts are not loaded');
      }
      const clients = accounts.clients;
      if (typeof account === 'string') {
        const acc = clients.flatMap(client => client.accounts).find(acc => acc.accountNumber === account);
        if (!acc) {
          logger.error(`Cannot select account, because it doesn't exist: ${account}`, { clients });
          throw new Error(`Cannot select account, because it doesn't exist: ${account}`);
        }
        updateSelectedAccountOrClient({ account: acc, client: undefined });
      } else {
        updateSelectedAccountOrClient({ account, client: undefined });
      }
    },
    [updateSelectedAccountOrClient, accounts, logger],
  );

  /**
   * Selects a client (and every account of it)
   * @param client Client id. If it's not specified the currently selected account's client will be selected
   */
  const selectClient = useCallback(
    (client?: string) => {
      if (!isDefined(client)) {
        if (selectedAccountOrClient?.account) {
          const client = selectedAccountOrClient.account.clientId;
          updateSelectedAccountOrClient({ account: undefined, client, accounts: getAccountsOfClient(client) });
        } else {
          // a client is already selected, but nothing is specified, so do nothing
        }
      } else {
        updateSelectedAccountOrClient({ account: undefined, client, accounts: getAccountsOfClient(client) });
      }
    },
    [updateSelectedAccountOrClient, selectedAccountOrClient, getAccountsOfClient],
  );

  /**
   * Returns the selected account or client or shows the account selector popover
   * if there is no selected account and the allowMultipleAccountSelection is false
   *
   * @returns Promise of the selected account or client, or an array of accounts if
   * allowMultipleAccountSelection is true and fails if the user doesn't select anything but allowMultipleAccountSelection is false
   */
  const getSelectedAccount = useCallback(
    <T extends boolean>(
      allowMultipleAccountSelection?: T,
      disabledCategories?: Array<AccountCategory>,
    ): Promise<T extends true ? Array<Account> : Account | null> => {
      setSelectAllAccountEnabled(!!allowMultipleAccountSelection);

      if (
        (!allowMultipleAccountSelection && !selectedAccountOrClient?.account) ||
        (allowMultipleAccountSelection && !selectedAccountOrClient?.client)
      ) {
        return new Promise<T extends true ? Array<Account> : Account | null>((resolve, reject) => {
          setAllowClientSelection(!!allowMultipleAccountSelection);
          setDisabledAccountCategories(disabledCategories || []);
          setAccountSelectPromise({ resolve: resolve as (value: Array<Account> | Account | null) => void, reject });
          setAccountSelectorPopover(accountSelectorButton);
        });
      } else if (!allowMultipleAccountSelection && selectedAccountOrClient?.account) {
        return Promise.resolve(selectedAccountOrClient.account) as Promise<T extends true ? Array<Account> : Account>;
      } else if (allowMultipleAccountSelection && selectedAccountOrClient?.client) {
        return Promise.resolve(getAccountsOfClient(selectedAccountOrClient.client)) as Promise<
          T extends true ? Array<Account> : Account
        >;
      } else {
        throw new Error('Invalid state');
      }
    },
    [
      accountSelectorButton,
      setAccountSelectorPopover,
      setAccountSelectPromise,
      setSelectAllAccountEnabled,
      selectedAccountOrClient,
      getAccountsOfClient,
    ],
  );

  /**
   * Opens the account selector popover without backdrop
   */
  const openAccountSelector = useCallback(() => {
    setAccountSelectorPopover(accountSelectorButton);
    setAllowClientSelection(true);
  }, [accountSelectorButton, setAccountSelectorPopover, setAllowClientSelection]);

  /**
   * Sets the account selector button reference, so we can open the popover on it
   */
  const initAccountSelectorButton = useCallback(
    (button: HTMLButtonElement | null) => {
      setAccountSelectorButton(button);
    },
    [setAccountSelectorButton],
  );

  /**
   * Sets the default selected account to the first account of the first client
   */
  useLayoutEffect(() => {
    if (!accounts.isLoaded) {
      return;
    }
    const clients = accounts.clients;

    const lastSelectedAccount = getLastSelectedAccount();

    if (lastSelectedAccount?.account) {
      const allAccount = clients.map(client => client.accounts).flat();
      const accountToSelect = allAccount.find(acc => acc.accountNumber === lastSelectedAccount.account.accountNumber);

      if (accountToSelect) {
        setSelectedAccountOrClient({
          account: accountToSelect,
          client: undefined,
        });

        return;
      }
    }

    if (lastSelectedAccount?.client) {
      const clientToSelect = clients.find(client => client.clientId === lastSelectedAccount.client);

      if (clientToSelect) {
        setSelectedAccountOrClient({
          account: undefined,
          client: clientToSelect.clientId,
          accounts: getAccountsOfClient(clientToSelect.clientId),
        });

        return;
      }
    }

    if (clients.length) {
      const firstClient = clients[0];
      if (firstClient.accounts.length > 1) {
        setSelectedAccountOrClient({
          account: undefined,
          client: clients[0].clientId,
          accounts: getAccountsOfClient(clients[0].clientId),
        });
      } else {
        setSelectedAccountOrClient({ account: clients[0].accounts[0], client: undefined });
      }
    }
  }, [accounts, getAccountsOfClient, getLastSelectedAccount]);

  return (
    <AccountContext.Provider
      value={{
        getSelectedAccount,
        initAccountSelectorButton,
        selectedAccountOrClient,
        openAccountSelector,
        selectAccount,
        selectClient,
        isAccountSelectorOpen: !!accountSelectorPopover,
        getAccountsOfClient,
        getAccountsOfClientByAccount,
        closeAccountPopover,
      }}
    >
      {children}

      <Popover
        open={!!accountSelectorPopover}
        anchorEl={accountSelectorPopover}
        onClose={() => handleAccountsClose(undefined)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        classes={{ paper: classes.popover }}
        hideBackdrop={!allowClientSelection}
        transitionDuration={{ enter: 300, exit: 0 }}
      >
        {accounts.isLoaded && selectedAccountOrClient && (
          <AccountsPopoverContent
            clients={accounts.clients}
            selectedAccountOrClient={selectedAccountOrClient}
            onClose={handleAccountsClose}
            selectAllAccountEnabled={selectAllAccountEnabled}
            allowClientSelection={allowClientSelection}
            getAccountsOfClient={getAccountsOfClient}
            disabledAccountCategories={disabledAccountCategories}
          />
        )}
      </Popover>
    </AccountContext.Provider>
  );
};
